import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, useMutation, gql } from '@apollo/client';
import StudentProfile from '../../components/StudentPlatform/pages/StudentProfile';
import ApolloWrapper from './ApolloWrapper';

const GET_STUDENT_PROFILE = gql`
  query GetStudentProfile {
    currentUser {
      id
      name
      email
    }
    studentProfile {
      id
      candidate_track
      age
      phone
      city
      state
      school
      degree_program
      graduation_year
      employment_type
      experience_level
      desired_location
      candidate_interests
      why_interested
      family_in_business
      linkedin_url
      resume_url
      video_url
      avatar_url
      certifications
      profile_completion_percentage
    }
  }
`;

const UPDATE_STUDENT_PROFILE = gql`
  mutation UpdateStudentProfile(
    $age: Int
    $phone: String
    $city: String
    $state: String
    $school: String
    $degree_program: String
    $graduation_year: Int
    $employment_type: String
    $experience_level: String
    $desired_location: String
    $candidate_interests: JSON
    $why_interested: String
    $family_in_business: Boolean
    $linkedin_url: String
    $certifications: JSON
  ) {
    updateStudentProfile(
      age: $age
      phone: $phone
      city: $city
      state: $state
      school: $school
      degreeProgram: $degree_program
      graduationYear: $graduation_year
      employmentType: $employment_type
      experienceLevel: $experience_level
      desiredLocation: $desired_location
      candidateInterests: $candidate_interests
      whyInterested: $why_interested
      familyInBusiness: $family_in_business
      linkedinUrl: $linkedin_url
      certifications: $certifications
    ) {
      id
      profile_completion_percentage
    }
  }
`;

const UPLOAD_FILE = gql`
  mutation UploadStudentFile(
    $file: String!
    $filename: String!
    $contentType: String!
    $fileType: String!
  ) {
    uploadStudentFile(
      file: $file
      filename: $filename
      contentType: $contentType
      fileType: $fileType
    ) {
      url
      success
      message
    }
  }
`;

const StudentProfileContainerInner = ({
  editMode = false,
  brandLogoUrl,
  primaryColor,
  secondaryColor,
  accentColor,
  neutralColor,
}) => {
  const { loading, error, data, refetch } = useQuery(GET_STUDENT_PROFILE);
  const [updateProfile] = useMutation(UPDATE_STUDENT_PROFILE);
  const [uploadFile] = useMutation(UPLOAD_FILE);

  const handleSave = async (formData, resumeFile, videoFile) => {
    try {
      // Update profile data
      await updateProfile({
        variables: {
          age: formData.age ? parseInt(formData.age) : null,
          phone: formData.phone,
          city: formData.city,
          state: formData.state,
          school: formData.school,
          degree_program: formData.degreeProgram,
          graduation_year: formData.graduationYear ? parseInt(formData.graduationYear) : null,
          employment_type: formData.employmentType,
          experience_level: formData.experienceLevel,
          desired_location: formData.desiredLocation,
          candidate_interests: formData.candidateInterests,
          why_interested: formData.whyInterested,
          family_in_business: formData.familyInBusiness,
          linkedin_url: formData.linkedinUrl,
          certifications: formData.certifications,
        },
      });

      // Upload resume if provided
      if (resumeFile) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = e.target.result.split(',')[1];
          await uploadFile({
            variables: {
              file: base64,
              filename: resumeFile.name,
              contentType: resumeFile.type,
              fileType: 'resume',
            },
          });
        };
        reader.readAsDataURL(resumeFile);
      }

      // Upload video if provided
      if (videoFile) {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = e.target.result.split(',')[1];
          await uploadFile({
            variables: {
              file: base64,
              filename: videoFile.name,
              contentType: videoFile.type,
              fileType: 'video',
            },
          });
        };
        reader.readAsDataURL(videoFile);
      }

      // Refetch to get updated data
      await refetch();
      alert('Profile updated successfully!');
    } catch (err) {
      console.error('Profile update error:', err);
      alert('Failed to update profile. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600">Error loading profile</p>
          <p className="text-gray-600 mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <StudentProfile
      currentUser={data?.currentUser}
      studentProfile={data?.studentProfile}
      editMode={editMode}
      onSave={handleSave}
      brandLogoUrl={brandLogoUrl}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      accentColor={accentColor}
      neutralColor={neutralColor}
    />
  );
};

StudentProfileContainerInner.propTypes = {
  editMode: PropTypes.bool,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

const StudentProfileContainer = (props) => (
  <ApolloWrapper>
    <StudentProfileContainerInner {...props} />
  </ApolloWrapper>
);

StudentProfileContainer.propTypes = {
  editMode: PropTypes.bool,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

export default StudentProfileContainer;

